/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetBillingEmailRequest,
  GetBillingEmailSecurity,
} from "../models/operations/getbillingemail.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetBillingEmailQuery,
  GetBillingEmailQueryData,
  prefetchGetBillingEmail,
  queryKeyGetBillingEmail,
} from "./getBillingEmail.core.js";
export {
  buildGetBillingEmailQuery,
  type GetBillingEmailQueryData,
  prefetchGetBillingEmail,
  queryKeyGetBillingEmail,
};

export type GetBillingEmailQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getBillingEmail usage
 *
 * @remarks
 * Get the billing notification email for a PAYG organization
 */
export function useGetBillingEmail(
  request?: GetBillingEmailRequest | undefined,
  security?: GetBillingEmailSecurity | undefined,
  options?: QueryHookOptions<
    GetBillingEmailQueryData,
    GetBillingEmailQueryError
  >,
): UseQueryResult<GetBillingEmailQueryData, GetBillingEmailQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetBillingEmailQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getBillingEmail usage
 *
 * @remarks
 * Get the billing notification email for a PAYG organization
 */
export function useGetBillingEmailSuspense(
  request?: GetBillingEmailRequest | undefined,
  security?: GetBillingEmailSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetBillingEmailQueryData,
    GetBillingEmailQueryError
  >,
): UseSuspenseQueryResult<GetBillingEmailQueryData, GetBillingEmailQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetBillingEmailQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetBillingEmailData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: GetBillingEmailQueryData,
): GetBillingEmailQueryData | undefined {
  const key = queryKeyGetBillingEmail(...queryKeyBase);

  return client.setQueryData<GetBillingEmailQueryData>(key, data);
}

export function invalidateGetBillingEmail(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "usage", "getBillingEmail", ...queryKeyBase],
  });
}

export function invalidateAllGetBillingEmail(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "usage", "getBillingEmail"],
  });
}
